import React, { ReactNode } from 'react'
import { InputCheckbox, CheckboxInternalProps } from 'library/atoms/Checkbox'
import styled from '@emotion/styled'
import { KeyEvents } from 'shared/enums/keys'
import { useField } from 'shared/providers/FormProvider/formProvider'

interface ContentCheckboxProps {
  children?: ReactNode
  name: string
  disabled?: boolean
  /**
   * Allows more props. corresponds to ...other
   */
  [propName: string]: unknown
}

/**
 * ContentCheckbox is used in place of a traditional `<input type="checkbox" />` element. This has
 * Formik baked into it for ease of use.
 *
 * We are not using the Checkbox component to avoid using duplicate labels.
 *
 * It uses styled-system to add margins if necessary, e.g. a section of
 * two inputs in the same row and the second input needs margin-let.
 */
const ContentCheckbox = (props: ContentCheckboxProps) => {
  const { name, children, disabled, ...other } = props
  const { field, meta, helpers } = useField<boolean>(name)
  const checked = field.value

  /**
   * Allow the 'checkbox' to be checked and unchecked via
   * the space bar, and prevent browser page scroll.
   */
  const handleWrapperKeyDown = (e: React.KeyboardEvent) => {
    if (KeyEvents.spacebar === e.key) {
      e.preventDefault()
      helpers.setValue(!field.value)
    }
  }

  return (
    <StyledContentCheckboxWrapper
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      aria-invalid={!!meta.error}
      checked={checked}
      disabled={disabled}
      {...other}
      onClick={() => {
        helpers.setValue(!checked)
      }}
      onKeyDown={handleWrapperKeyDown}
    >
      {/**
       * The &nbsp is present here to create space between children and InputCheckbox.
       * It is not possible to use the margin attribute because the parent element has
       * display flex and it would affect the design.
       * */}
      {children && <>{children}&nbsp;&nbsp;&nbsp;</>}
      <InputCheckbox name={name} disabled={disabled} />
    </StyledContentCheckboxWrapper>
  )
}

export default ContentCheckbox

const StyledContentCheckboxWrapper = styled.div<CheckboxInternalProps>(
  ({ theme, checked, disabled }) => {
    const VARIANTS: {
      [key: string]: {
        labelColor: string
        textColor: string
        backgroundColor: string
        pointerEvents: 'auto' | 'none'
      }
    } = {
      default: {
        labelColor: theme.colors.primary,
        textColor: theme.colors.text.secondary,
        backgroundColor: theme.colors.white,
        pointerEvents: 'auto'
      },
      checked: {
        labelColor: theme.colors.white,
        textColor: theme.colors.white,
        backgroundColor: theme.colors.primary,
        pointerEvents: 'auto'
      },
      disabled: {
        labelColor: theme.colors.state.disabled,
        textColor: theme.colors.state.disabled,
        backgroundColor: theme.colors.white,
        pointerEvents: 'none'
      }
    }

    const variant = checked ? 'checked' : disabled ? 'disabled' : 'default'
    const { labelColor, textColor, backgroundColor, pointerEvents } =
      VARIANTS[variant]

    return {
      color: labelColor,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      height: 'auto',
      padding: theme.space[4],
      border: theme.border,
      borderRadius: theme.radii[3],
      backgroundColor: backgroundColor,
      cursor: 'pointer',
      pointerEvents: pointerEvents,
      userSelect: 'none',
      /**
       * When adding child SVG this component should overwrite the color for different states
       */
      svg: {
        pointerEvents: pointerEvents,
        use: {
          color: labelColor
        }
      },
      p: {
        color: textColor
      },
      label: {
        ...theme.mixins.capitalizeFirstLetter()
      },
      '&:hover': {
        transition: `${theme.transitionTime[1]}s ease`,
        border: `1px solid ${theme.colors.primary}`,
        boxShadow: theme.shadows.primary[1]
      },
      '[role="checkbox"] + &[role="checkbox"]': {
        marginTop: theme.space[4]
      }
    }
  }
)
